<section class="audit-creator">
  <div class="audit-creator__header">
    <div>
      <div class="audit-creator__eyebrow">{{ i18n.t('auditCreator.eyebrow') }}</div>
      <h2 class="audit-creator__title">{{ i18n.t('auditCreator.title') }}</h2>
    </div>
  </div>

  <ol class="app-stepper audit-creator__stepper">
    <li
      class="app-stepper__item app-stepper__item--clickable"
      [class.app-stepper__item--active]="currentStep() === 1"
      [class.app-stepper__item--complete]="currentStep() > 1"
      (click)="goToStep(1)"
    >
      <span class="app-stepper__index">1</span>
      <div class="app-stepper__copy">
        <div class="app-stepper__title">{{ i18n.t('auditCreator.steps.definition.title') }}</div>
        <div class="app-stepper__meta">{{ i18n.t('auditCreator.steps.definition.meta') }}</div>
      </div>
      @if (currentStep() > 1) {
        <span class="app-stepper__complete-icon fa-solid fa-check" aria-hidden="true"></span>
      }
    </li>

    <li
      class="app-stepper__item"
      [class.app-stepper__item--active]="currentStep() === 2"
      [class.app-stepper__item--clickable]="hasSavedAudit()"
      (click)="goToStep(2)"
    >
      <span class="app-stepper__index">2</span>
      <div class="app-stepper__copy">
        <div class="app-stepper__title">{{ i18n.t('auditCreator.steps.locations.title') }}</div>
        <div class="app-stepper__meta">{{ i18n.t('auditCreator.steps.locations.meta') }}</div>
      </div>
      @if (currentStep() > 2) {
        <span class="app-stepper__complete-icon fa-solid fa-check" aria-hidden="true"></span>
      }
    </li>

    <li
      class="app-stepper__item"
      [class.app-stepper__item--active]="currentStep() === 3"
      [class.app-stepper__item--clickable]="hasSavedAuditLocations()"
      (click)="goToStep(3)"
    >
      <span class="app-stepper__index">3</span>
      <div class="app-stepper__copy">
        <div class="app-stepper__title">{{ i18n.t('auditCreator.steps.schedule.title') }}</div>
        <div class="app-stepper__meta">{{ i18n.t('auditCreator.steps.schedule.meta') }}</div>
      </div>
    </li>
  </ol>

  @if (currentStep() === 1) {
    <section class="app-panel">
      <div class="app-panel__header">
        <h3 class="app-panel__title">{{ i18n.t('auditCreator.definitionSection.title') }}</h3>
        <p class="app-panel__meta">{{ i18n.t('auditCreator.definitionSection.meta') }}</p>
      </div>

      <form class="audit-creator__form-grid">
        <label class="app-field" [class.app-field--invalid]="nameValidationVisible()">
          <span class="app-field__label">{{ i18n.t('auditCreator.fields.name') }}</span>
          <input
            type="text"
            [placeholder]="i18n.t('auditCreator.placeholders.name')"
            [value]="auditName()"
            [disabled]="isDefinitionLocked()"
            (input)="updateAuditName(($any($event.target)).value)"
          />
        </label>

        <label class="app-field audit-creator__field-full">
          <span class="app-field__label">{{ i18n.t('auditCreator.fields.description') }}</span>
          <textarea
            class="app-textarea"
            [placeholder]="i18n.t('auditCreator.placeholders.description')"
            [value]="auditDescription()"
            [disabled]="isDefinitionLocked()"
            (input)="updateAuditDescription(($any($event.target)).value)"
          ></textarea>
        </label>
      </form>

      @if (nameValidationVisible()) {
        <div class="form-message"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ i18n.t('auditCreator.errors.nameRequired') }}</div>
      }

      <section class="app-panel app-panel--nested audit-creator__checklists">
        <div class="app-panel__header">
          <h4 class="app-panel__title">{{ i18n.t('auditCreator.checklists.title') }}</h4>
          <p class="app-panel__meta">{{ i18n.t('auditCreator.checklists.meta') }}</p>
        </div>

        @if (checklistLoadError(); as error) {
          <div class="form-message"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ error }}</div>
        }

        @if (isLoadingChecklists()) {
          <div class="app-empty-state"><span class="app-message__icon app-message__icon--loading fa-solid fa-spinner fa-spin" aria-hidden="true"></span> {{ i18n.t('auditCreator.checklists.loading') }}</div>
        } @else if (!hasAvailableChecklists()) {
          <div class="app-empty-state"><span class="app-message__icon app-message__icon--info fa-solid fa-circle-info" aria-hidden="true"></span> {{ i18n.t('auditCreator.checklists.empty') }}</div>
        } @else {
          <div class="audit-creator__checklist-list">
          @for (checklist of availableChecklists(); track checklist.id) {
            <label class="app-list-row audit-creator__checklist-row">
              <input
                class="audit-creator__checkbox"
                type="checkbox"
                [checked]="isChecklistSelected(checklist.id)"
                [disabled]="isDefinitionLocked()"
                (change)="toggleChecklist(checklist.id)"
              />
              <span class="audit-creator__list-row-copy">
                <span class="app-list-row__title">{{ checklist.title }}</span>
                <span class="audit-creator__group-badges">
                  @for (group of checklist.groups ?? []; track group.id) {
                    <span class="audit-creator__group-badge">
                      {{ group.name }}
                      <strong>{{ getGroupQuestionsCount(group) }}</strong>
                    </span>
                  }
                </span>
                <span class="app-list-row__meta audit-creator__checklist-meta">
                  {{ i18n.t('auditCreator.checklists.versionPrefix') }} {{ checklist.version }}
                  <span aria-hidden="true"> | </span>
                  {{ i18n.t('auditCreator.checklists.createdAtPrefix') }} {{ formatCreatedAt(checklist.created_at) }}
                  <span aria-hidden="true"> | </span>
                  {{ i18n.t('auditCreator.checklists.questionsCount') }} {{ getChecklistQuestionsCount(checklist) }}
                </span>
              </span>
            </label>
          }
          </div>
        }
      </section>

      @if (auditSaveError(); as error) {
        <div class="form-message"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ error }}</div>
      }

      <div class="app-panel__actions">
        <button class="secondary-button secondary-button--indigo audit-creator__close-action" type="button" (click)="close()">
          {{ i18n.t('auditCreator.actions.close') }}
        </button>
        <button class="primary-button" type="button" [disabled]="isSavingAudit()" (click)="saveAuditAndContinue()">
          @if (isSavingAudit()) {
            {{ isEditingAudit() ? i18n.t('auditCreator.actions.updatingAudit') : i18n.t('auditCreator.actions.savingAudit') }}
          } @else {
            {{ isEditingAudit() ? i18n.t('auditCreator.actions.updateAndContinueToLocations') : i18n.t('auditCreator.actions.saveAndContinueToLocations') }}
          }
        </button>
      </div>
    </section>
  }

  @if (currentStep() === 2) {
    <section class="app-panel">
      <div class="app-panel__header">
        <h3 class="app-panel__title">{{ i18n.t('auditCreator.locationsSection.title') }}</h3>
        <p class="app-panel__meta">{{ i18n.t('auditCreator.locationsSection.meta') }}</p>
      </div>

      @if (auditLocationsLoadError(); as error) {
        <div class="form-message"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ error }}</div>
      }

      <div class="audit-creator__form-grid audit-creator__location-form">
        <app-multi-select
          controlId="audit-locations"
          [label]="i18n.t('auditCreator.fields.locations')"
          [placeholder]="i18n.t('auditCreator.placeholders.locations')"
          [options]="locationOptions()"
          [selectedValues]="selectedLocationIds()"
          [disabled]="isSavingAuditLocations()"
          (selectionChanged)="updateSelectedLocations($event)"
        />
        <app-multi-select
          controlId="audit-roles"
          [label]="i18n.t('auditCreator.fields.roles')"
          [placeholder]="i18n.t('auditCreator.placeholders.roles')"
          [options]="roleOptions()"
          [selectedValues]="selectedRoleIds()"
          [disabled]="isSavingAuditLocations()"
          (selectionChanged)="updateSelectedRoles($event)"
        />

        <div class="audit-creator__location-add">
          <button
            class="primary-button"
            type="button"
            [disabled]="!selectedLocationIds().length || !selectedRoleIds().length || isSavingAuditLocations()"
            (click)="addAuditLocation()"
          >
            {{ i18n.t('auditCreator.actions.addAuditLocations') }} ({{ pendingAuditLocations().length }})
          </button>
        </div>
      </div>

      @if (auditLocationsValidationError(); as error) {
        <div class="form-message"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ error }}</div>
      }

      @if (!hasAvailableLocations()) {
        <div class="app-empty-state"><span class="app-message__icon app-message__icon--warning fa-solid fa-triangle-exclamation" aria-hidden="true"></span> {{ i18n.t('auditCreator.locations.emptyLocations') }}</div>
      } @else if (!hasAvailableRoles()) {
        <div class="app-empty-state"><span class="app-message__icon app-message__icon--warning fa-solid fa-triangle-exclamation" aria-hidden="true"></span> {{ i18n.t('auditCreator.locations.emptyRoles') }}</div>
      }

      <section class="app-panel app-panel--nested audit-creator__assigned-locations">
        <div class="app-panel__header">
          <h4 class="app-panel__title">{{ i18n.t('auditCreator.locations.listTitle') }}</h4>
          <p class="app-panel__meta">{{ i18n.t('auditCreator.locations.listMeta') }}</p>
        </div>

        @if (!auditLocations().length) {
          <div class="app-empty-state"><span class="app-message__icon app-message__icon--info fa-solid fa-circle-info" aria-hidden="true"></span> {{ i18n.t('auditCreator.locations.emptyAssignments') }}</div>
        } @else {
          <div class="audit-creator__assignment-list">
            @for (item of auditLocations(); track item.location_id + '-' + item.role_id; let index = $index) {
              <div class="app-list-row audit-creator__assignment-row">
                <span class="audit-creator__assignment-icon fa-solid fa-location-dot" aria-hidden="true"></span>
                <span class="audit-creator__list-row-copy">
                  <span class="app-list-row__title">{{ getLocationName(item.location_id) }}</span>
                  <span class="app-list-row__meta">
                    {{ i18n.t('auditCreator.fields.role') }}: {{ getRoleName(item.role_id) }}
                  </span>
                </span>
                <button
                  class="app-button--danger secondary-button audit-creator__remove-assignment"
                  type="button"
                  [disabled]="isSavingAuditLocations()"
                  (click)="removeAuditLocation(index)"
                >
                  {{ i18n.t('auditCreator.actions.removeAuditLocation') }}
                </button>
              </div>
            }
          </div>
        }
      </section>

      @if (auditLocationsSaveError(); as error) {
        <div class="form-message"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ error }}</div>
      }

      <div class="app-panel__actions">
        <button class="secondary-button secondary-button--indigo audit-creator__close-action" type="button" (click)="close()">
          {{ i18n.t('auditCreator.actions.close') }}
        </button>
        <button
          class="primary-button"
          type="button"
          [disabled]="isSavingAuditLocations()"
          (click)="saveAuditLocationsAndContinue()"
        >
          {{ isSavingAuditLocations()
            ? i18n.t('auditCreator.actions.savingAuditLocations')
            : i18n.t('auditCreator.actions.saveAuditLocationsAndContinue') }}
        </button>
      </div>
    </section>
  }

  @if (currentStep() === 3) {
    <section class="app-panel">
      <div class="app-panel__header">
        <h3 class="app-panel__title">{{ i18n.t('auditCreator.scheduleSection.title') }}</h3>
        <p class="app-panel__meta">{{ i18n.t('auditCreator.scheduleSection.meta') }}</p>
      </div>

      <div class="audit-creator__frequency-grid" role="group" [attr.aria-label]="i18n.t('auditCreator.fields.frequency')">
        @for (frequency of ['one_time', 'daily', 'weekly', 'monthly', 'custom_days', 'annual']; track frequency) {
          <button
            class="audit-creator__frequency-option"
            type="button"
            [attr.aria-pressed]="selectedFrequency() === frequency"
            [disabled]="isSavingAssignment()"
            [class.audit-creator__frequency-option--active]="selectedFrequency() === frequency"
            (click)="setFrequency($any(frequency))"
          >
            <span class="audit-creator__frequency-icon fa-solid fa-calendar-days" aria-hidden="true"></span>
            <span>{{ i18n.t('auditCreator.frequency.' + frequency) }}</span>
          </button>
        }
      </div>

      @if (selectedFrequency() === 'annual') {
        <div class="audit-creator__annual-plan">
          <label class="app-field audit-creator__annual-year">
            <span class="app-field__label">{{ i18n.t('auditCreator.annual.year') }}</span>
            <input type="number" min="1" max="9999" step="1" required
              [value]="annualYear()" [disabled]="isSavingAssignment()"
              (input)="updateAnnualYear($any($event.target).value)" />
          </label>
          <p class="app-panel__meta">{{ i18n.t('auditCreator.annual.description') }}</p>
          @if (!auditLocations().length) {
            <div class="app-empty-state">{{ i18n.t('auditCreator.locations.emptyAssignments') }}</div>
          }
          @for (item of auditLocations(); track item.location_id + ':' + item.role_id) {
            <div class="app-list-row audit-creator__annual-row">
              <span class="audit-creator__list-row-copy">
                <span class="app-list-row__title" [id]="'annual-location-' + item.location_id + '-' + item.role_id">{{ getLocationName(item.location_id) }}</span>
                <span class="app-list-row__meta" [id]="'annual-role-' + item.location_id + '-' + item.role_id">{{ i18n.t('auth.account.roles.' + getRoleName(item.role_id)) }}</span>
              </span>
              <label class="app-field audit-creator__annual-count" [class.app-field--invalid]="annualValidationVisible() && annualCountInvalid(item)">
                <span class="app-field__label" [id]="'annual-label-' + item.location_id + '-' + item.role_id">{{ i18n.t('auditCreator.annual.count') }}</span>
                <input type="number" min="1" max="99" step="1" required inputmode="numeric" placeholder="1–99"
                  [attr.aria-labelledby]="'annual-label-' + item.location_id + '-' + item.role_id + ' annual-location-' + item.location_id + '-' + item.role_id + ' annual-role-' + item.location_id + '-' + item.role_id"
                  [attr.aria-invalid]="annualValidationVisible() && annualCountInvalid(item)"
                  [value]="annualCount(item)" [disabled]="isSavingAssignment()"
                  (input)="updateAnnualCount(item, $any($event.target).value)" />
              </label>
            </div>
          }
        </div>
      } @else {
      <form class="audit-creator__form-grid">
        @if (selectedFrequency() === 'one_time') {
          <label class="app-field">
            <span class="app-field__label">{{ i18n.t('auditCreator.fields.executionDate') }}</span>
            <input
              type="date"
              [value]="executionDate()"
              (input)="updateExecutionDate(($any($event.target)).value)"
            />
          </label>
        } @else {
          <label class="app-field">
            <span class="app-field__label">{{ i18n.t('auditCreator.fields.startDate') }}</span>
            <input
              type="date"
              [value]="startDate()"
              (input)="updateStartDate(($any($event.target)).value)"
            />
          </label>

          <label class="app-field">
            <span class="app-field__label">{{ i18n.t('auditCreator.fields.endDate') }}</span>
            <input
              type="date"
              [value]="endDate()"
              (input)="updateEndDate(($any($event.target)).value)"
            />
          </label>
        }

        <label class="app-field">
          <span class="app-field__label">{{ i18n.t('auditCreator.fields.startTime') }}</span>
          <input type="time" min="00:00" max="23:59" step="60" required [value]="startTime()"
            [disabled]="isSavingAssignment()" (input)="startTime.set($any($event.target).value)" />
        </label>
        <label class="app-field">
          <span class="app-field__label">{{ i18n.t('auditCreator.fields.endTime') }}</span>
          <input type="time" min="00:00" max="23:59" step="60" required [value]="endTime()"
            [disabled]="isSavingAssignment()" (input)="endTime.set($any($event.target).value)" />
        </label>
        <p class="app-panel__meta audit-creator__field-full">{{ i18n.t('auditCreator.scheduleSection.timeWindow') }}</p>

        @if (selectedFrequency() === 'weekly' || selectedFrequency() === 'custom_days') {
          <div class="audit-creator__field-full audit-creator__weekday-section">
            <div class="app-panel__header">
              <h4 class="app-panel__title">{{ i18n.t('auditCreator.weekdays.title') }}</h4>
              <p class="app-panel__meta">{{ i18n.t('auditCreator.weekdays.meta') }}</p>
            </div>
            <div class="audit-creator__weekday-list">
              @for (weekday of weekdays; track weekday.value) {
                <label class="audit-creator__weekday">
                  <input
                    type="checkbox"
                    [checked]="isWeekdaySelected(weekday.value)"
                    (change)="toggleWeekday(weekday.value)"
                  />
                  <span>{{ i18n.t(weekday.labelKey) }}</span>
                </label>
              }
            </div>
          </div>
        }

        @if (selectedFrequency() === 'monthly') {
          <label class="app-field">
            <span class="app-field__label">{{ i18n.t('auditCreator.fields.dayOfMonth') }}</span>
            <input
              type="number"
              min="1"
              max="31"
              [placeholder]="i18n.t('auditCreator.placeholders.dayOfMonth')"
              [value]="dayOfMonth()"
              (input)="updateDayOfMonth(($any($event.target)).value)"
            />
          </label>
        }
      </form>
      }

      @if (assignmentValidationError(); as error) {
        <div class="form-message"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ error }}</div>
      }

      @if (assignmentSaveError(); as error) {
        <div class="form-message"><span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span> {{ error }}</div>
      }

      <div class="app-panel__actions">
        <button class="secondary-button secondary-button--indigo audit-creator__close-action" type="button" (click)="close()">
          {{ i18n.t('auditCreator.actions.close') }}
        </button>
        <button class="primary-button" type="button" [disabled]="isSavingAssignment() || isLoadingChecklists() || !!checklistLoadError()" (click)="saveAssignment()">
          {{ isSavingAssignment() ? i18n.t('auditCreator.actions.savingAssignment') : i18n.t(selectedFrequency() === 'annual' ? 'auditCreator.annual.save' : 'auditCreator.actions.saveSchedule') }}
        </button>
      </div>
    </section>
  }

  <app-confirmation-dialog
    [isOpen]="checklistRequiredDialogOpen()"
    [title]="i18n.t('auditCreator.checklistRequiredDialog.title')"
    [message]="i18n.t('auditCreator.checklistRequiredDialog.message')"
    [confirmLabel]="i18n.t('common.actions.ok')"
    [cancelLabel]="i18n.t('common.actions.cancel')"
    [showCancel]="false"
    (confirmed)="closeChecklistRequiredDialog()"
    (cancelled)="closeChecklistRequiredDialog()"
  />
</section>
